import {
	CSSProperties,
	ImgHTMLAttributes,
	SyntheticEvent,
	useContext,
	useEffect,
	useRef,
	useState,
} from "react";
import classNames from "classnames/bind";
import AppGlobalsContext from "app/AppGlobalsContext";
import type { ImageQuality } from "./cloudinaryImage";
import { cloudinaryLoader, ImageLoader } from "./imageLoader";
import { getSrcSetWidths } from "./srcSetWidths";
import styles from "./Image.module.scss";

const cx = classNames.bind(styles);

const NO_IMAGE_PATH = "/icons/no-image.png";

export type ImageProps = {
	src: string;
	/** Required. Use `alt=""` for purely decorative images. */
	alt: string;
	/** Display box ratio (only the ratio matters) — sets aspect-ratio to reserve space (CLS). */
	width: number;
	height: number;
	/** Required: the real display width per breakpoint, e.g. "(max-width: 768px) 100vw, 25vw". */
	sizes: string;
	/** Bring your own srcset; otherwise it's generated by the loader. */
	srcSet?: string;
	/** Blur placeholder source; derived by the loader when omitted. */
	thumbnail?: string;
	placeholder?: "blur" | "empty";
	/** Above-the-fold image → eager + high fetch priority (LCP). */
	priority?: boolean;
	/** Fill the positioned, sized parent (100% × cover) instead of reserving space via aspect-ratio. */
	fill?: boolean;
	/** Compression level of the file. `low` suits cards and thumbnails; ask for `good` on large visuals (hero, gallery). */
	quality?: ImageQuality;
	/** URL strategy — defaults to Cloudinary; swap for another CDN. */
	loader?: ImageLoader;
	/** Shown when `src` fails to load; defaults to the shared "no image" picture. */
	fallbackSrc?: string;
} & Omit<
	ImgHTMLAttributes<HTMLImageElement>,
	"src" | "alt" | "width" | "height" | "srcSet" | "sizes" | "loading" | "placeholder" | "style"
> & { style?: CSSProperties };

export const Image = ({
	src,
	alt,
	width,
	height,
	sizes,
	srcSet,
	thumbnail,
	placeholder = "blur",
	priority = false,
	fill = false,
	quality = "low",
	loader = cloudinaryLoader,
	fallbackSrc,
	onError,
	className,
	style,
	...rest
}: ImageProps) => {
	const { publicPath = "" } = useContext(AppGlobalsContext);
	const ref = useRef<HTMLImageElement>(null);
	const [loaded, setLoaded] = useState(false);
	const [failed, setFailed] = useState(false);
	const [renderedSrc, setRenderedSrc] = useState(src);

	if (renderedSrc !== src) {
		setRenderedSrc(src);
		setLoaded(false);
		setFailed(false);
	}

	useEffect(() => {
		const image = ref.current;

		if (!image?.complete) {
			return;
		}

		if (image.naturalWidth === 0) {
			setFailed(true);
		} else {
			setLoaded(true);
		}
	}, []);

	const handleError = (event: SyntheticEvent<HTMLImageElement>) => {
		onError?.(event);
		setFailed(true);
	};

	const lqip = placeholder === "blur" && !failed ? (thumbnail ?? loader.lqip(src)) : undefined;
	const resolvedSrc = failed ? (fallbackSrc ?? `${publicPath}${NO_IMAGE_PATH}`) : src;
	const resolvedSrcSet = failed
		? undefined
		: (srcSet ?? loader.srcSet(src, { width, height }, getSrcSetWidths(sizes), quality));
	const loading = priority ? "eager" : "lazy";

	return (
		<span
			className={cx("image", className)}
			data-fill={fill ? "" : undefined}
			style={{
				...(fill ? null : { aspectRatio: `${width} / ${height}` }),
				...style,
			}}
		>
			{lqip && (
				<img
					className={cx("image__placeholder")}
					src={lqip}
					alt=""
					aria-hidden="true"
					decoding="async"
					loading={loading}
				/>
			)}
			<img
				{...rest}
				ref={ref}
				className={cx("image__img")}
				data-loaded={loaded ? "" : undefined}
				data-failed={failed ? "" : undefined}
				src={resolvedSrc}
				srcSet={resolvedSrcSet}
				sizes={sizes}
				width={width}
				height={height}
				alt={alt}
				decoding="async"
				loading={loading}
				fetchPriority={priority ? "high" : "auto"}
				onLoad={() => setLoaded(true)}
				onError={handleError}
			/>
		</span>
	);
};
